/* eslint-disable consistent-return */
/* eslint-disable react/jsx-one-expression-per-line */
import React, { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import { Dropdown } from 'primereact/dropdown';

import SpinnerOverlay from '../../components/SpinnerOverlay/SpinnerOverlay';
import { handleError, getInitials, extractFirstMentionedFullName } from '../../utils';
import useAxios from '../../hooks/useAxios';
import { useAuth } from '../../hooks/useAuth';

import './Home.scss';

import PendingActionIcon from '../../assets/icons/dashboard-pending-action-icon.svg';
import AgencySubmissionIcon from '../../assets/icons/agency-submissions-icon.svg';
import InReviewIcon from '../../assets/icons/in-review-icon.svg';
import OverdueWorkflowIcon from '../../assets/icons/overdue-workflows-icon.svg';
import SummaryPendingIcon from '../../assets/icons/status-icons/pending-icon.svg';
import ActiveWorkflowsIcon from '../../assets/icons/active-workflows-icon.svg';
import UnderDiscussionIcon from '../../assets/icons/under-discussion-icon.svg';
import ReviewCompletedIcon from '../../assets/icons/review-completed-icon.svg';
import TotalWorkflowIcon from '../../assets/icons/total-workflows-icon.svg';
import ReturnChangesIcon from '../../assets/icons/return-changes-icon.svg';

const VerdictGraph = lazy(() => import('./VerdictGraph/VerdictGraph'));
const PendingWorkflowGraph = lazy(() => import('./PendingWorkflowGraph/PendingWorkflowGraph'));
const WorkflowTable = lazy(() => import('./WorkflowTable/WorkflowTable'));
const ActivityFeed = lazy(() => import('./ActivityFeed/ActivityFeed'));
const MentionList = lazy(() => import('./MentionList/MentionList'));

const Home = () => {
  const toast = useRef(null);
  const toastBottom = useRef(null);

  const { user } = useAuth();

  const reviewerRole = user.role === 'reviewer';
  const boAgencyRole = user.role === 'brand_owner' || user.role === 'agency';
  const adminRole = user.role === 'admin' || user.role === 'marcom';

  const defaultToggle = adminRole ? 'assigned_to_me'
    : reviewerRole
      ? 'pending_review'
      : 'active_workflows';

  const [isLoading, setisLoading] = useState<any>(false);
  const [filterTable, setFilterTable] = useState<any>('top_10');
  const [toggleFilterTable, setToggleFilterTable] = useState(localStorage.getItem('LastToggledOption') ?? defaultToggle);
  const [userActivityData, setUserActivityData] = useState<any>([]);
  const [filteredActivityData, setFilteredActivityData] = useState<any>([]);
  const [timePeriodValue, setTimePeriodValue] = useState<any>({ name: 'Last 30 days', code: 30 });
  const [pendingReview, setPendingReview] = useState<any>(0);
  const [pendingWorkflowData, setPendingWorkflowData] = useState<any>([]);
  const [allActiveWorkflows, setAllActiveWorkflows] = useState<any>([]);
  const [allCompleteWorkflows, setAllCompleteWorkflows] = useState<any>([]);
  const [activeWorkflowsCount, setActiveWorkflowsCount] = useState<any>(0);
  const [completeWorkflowsCount, setCompleteWorkflowsCount] = useState<any>(0);
  const [allAssignedWorkflows, setAllAssignedWorkflows] = useState<any>([]);
  const [assignedWorkflowCount, setAssignedWorkflowCount] = useState<any>(0);

  const [summaryData, setSummaryData] = useState<any>([]);
  const [verdictGraphData, setVerdictGraphData] = useState<any>({});
  const [pendingWorkflowGraphData, setPendingWorkflowGraphData] = useState<any>([]);
  const [mentionData, setMentionData] = useState<any>([]);

  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { get, post, apiLoading } = useAxios();

  const [activityFilterCategory, setActivityFilterCategory] = useState<any>('all');
  const [allWorkflows, setAllWorkflows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const savedSort: any = localStorage.getItem('dashbordSorting') ?? null;
  const [lazyState, setlazyState] = useState(JSON.parse(savedSort) ?? {
    first: 0,
    rows: 10,
    page: 0,
    sortField: 'created_datetime',
    sortOrder: -1,
  });

  let sortOrder = 'DESC';
  if (lazyState.sortOrder && lazyState.sortOrder === 1) {
    sortOrder = 'ASC';
  }
  const prevSortRef = useRef({
    sortField: lazyState.sortField,
    sortOrder: lazyState.sortOrder,
  });

  const onSort = (event: any) => {
    setlazyState((prevState) => ({
      ...prevState,
      sortField: event.sortField,
      sortOrder: event.sortOrder,
    }));
  };

  const timePeriodOptions = [
    { name: 'Last 7 days', code: 7 },
    { name: 'Last 30 days', code: 30 },
    { name: 'Last 60 days', code: 60 },
    { name: 'Last 90 days', code: 90 }
  ];

  const activityFilterOptions = [
    {
      name: 'All',
      value: 'all',
      category: [],
      action: []
    },
    {
      name: 'Annotations',
      value: 'annotations',
      category: ['annotation_file', 'file_attachment'],
      action: ['update_data', 'add_data', 'delete_data']
    },
    {
      name: 'Annotation Status',
      value: 'annotation_status',
      category: ['file_attachment'],
      action: ['status_change']
    },
    {
      name: 'Comments',
      value: 'comments',
      category: ['annotation_file_comment', 'file_attachment_comment'],
      action: ['add_data', 'delete_data']
    },
    {
      name: 'Materials Uploaded',
      value: 'materials_uploaded',
      category: ['annotation_file', 'file_attachment', 'workflow_asset'],
      action: ['upload']
    },
    {
      name: 'Materials Deleted',
      value: 'materials_deleted',
      category: ['workflow_asset', 'annotation_file'],
      action: ['delete']
    },
    {
      name: 'Materials Sent for Approval',
      value: 'materials_sent_for_approval',
      category: ['annotation_file', 'file_attachment'],
      action: []
    },
    {
      name: 'Verdict/Voting',
      value: 'verdict_voting',
      category: ['file_attachment'],
      action: ['vote']
    },
    {
      name: 'Workflow Status',
      value: 'workflow_status',
      category: ['workflow'],
      action: ['status_change']
    },
    {
      name: 'Agency Actions',
      value: 'agency_actions',
      category: ['annotation_file'],
      action: ['upload']
    },
    {
      name: 'Assets',
      value: 'assets',
      category: ['file_attachment'],
      action: []
    },
    {
      name: 'Workflow Updates',
      value: 'workflow_updates',
      category: ['workflow'],
      action: []
    }
  ];

  const onActivityFilterCategoryChange = (selected: any) => {
    if (selected === 'all') {
      setFilteredActivityData(userActivityData);
      setActivityFilterCategory('all');
    } else {
      const selectedOption: any = activityFilterOptions.find(opt => opt.value === selected);

      setActivityFilterCategory(selected);

      const tempData = userActivityData.filter((data: any) => {
        const matchesCategory = selectedOption.category.includes(data.category);
        const matchesAction = selectedOption.action.length === 0 || (data.action && selectedOption.action.includes(data.action));

        return matchesCategory && matchesAction;
      });

      setFilteredActivityData(tempData);
    }
  };

  const getUserBatch = (ids: any) => {
    const postData = {
      id_list: ids
    };

    try {
      const res = post('user-manager/batch_get_user_details', postData).then((response: any) => {
        const { status } = response;

        if ([200, 201].includes(status)) {
          if (response.data.messages && response.data.messages.length > 0) {
            return response.data.messages;
          }
        }
      });

      return res;
    } catch (error: any) {
      if ([401, 404, 500].includes(error?.request?.status)) {
        handleError(error, toastBottom);
      }
    }
  };

  const onTogglePageChange = (val: any) => {
    setToggleFilterTable(val);
    localStorage.setItem('LastToggledOption', val);
  };

  const getAllData = async (isSort = false, isWorkflowOnly = false) => {
    setisLoading(true);

    let apiPath = `/ai-search-manager/cosmos_db_get_dashboard_by_user_id?results_per_page=${lazyState.rows}&page_number=${lazyState.page + 1}&order_by=c.${lazyState.sortField} ${sortOrder}&number_of_days=${timePeriodValue.code}&timezone=${userTimeZone}`;

    if (isSort) {
      apiPath += '&workflows_only=true';
    }
    if (['marcom', 'admin'].includes(user.role)) {
      apiPath += '&get_all_if_admin=true';
    }
    if (isWorkflowOnly) {
      apiPath += '&workflow_list_only=true';
    }

    await get(apiPath).then((response: any) => {
      const { status } = response;
      setisLoading(false);
      localStorage.setItem('dashbordSorting', JSON.stringify(lazyState));
      if ([200, 201].includes(status)) {
        // getting top summary data 
        const summaryResponse = response?.data?.messages[0];
        if (!isWorkflowOnly) {
          if (['marcom', 'admin'].includes(user.role)) {
            const tempData = [
              { label: 'Active Workflows', value: summaryResponse?.active_workflows?.total, icon: ActiveWorkflowsIcon, className: 'active-workflows-card' },
              { label: 'Overdue Workflows', value: summaryResponse?.overdue_workflows.total, icon: SummaryPendingIcon, className: 'pending-workflow-card' },
              { label: 'Non Compliant Workflows', value: summaryResponse?.non_compliant_workflows.total, icon: InReviewIcon, className: 'in-review-card' },
              { label: 'Needs Meeting', value: summaryResponse?.materials_under_discussion.total, icon: UnderDiscussionIcon, className: 'under-discussion-card' },
            ];
            setSummaryData(tempData);
          } else if (['brand_owner'].includes(user.role)) {
            const tempData = [
              { label: 'Pending Action', value: summaryResponse?.pending_action.total, icon: PendingActionIcon, className: 'pending-action-card' },
              { label: 'Agency Submissions', value: summaryResponse?.agency_submissions.total, icon: AgencySubmissionIcon, className: 'agency-submissions-card' },
              { label: 'In Review', value: summaryResponse?.in_review.total, icon: InReviewIcon, className: 'in-review-card' },
              { label: 'Overdue Workflows', value: summaryResponse?.overdue_workflows.total, icon: OverdueWorkflowIcon, className: 'overdue-workflow-card' },
            ];
            setSummaryData(tempData);
          } else if (['reviewer'].includes(user.role)) {
            const tempData = [
              { label: 'Review Completed', value: summaryResponse?.review_completed.total, icon: ReviewCompletedIcon, className: 'review-completed-card' },
              { label: 'Pending Action', value: summaryResponse?.pending_action.total, icon: PendingActionIcon, className: 'pending-action-card' },
              { label: 'Overdue Workflows', value: summaryResponse?.overdue_workflows.total, icon: SummaryPendingIcon, className: 'pending-workflow-card' },
            ];
            setSummaryData(tempData);
          } else if (['agency'].includes(user.role)) {
            const tempData = [
              { label: 'Total Workflow', value: summaryResponse?.total_workflows.total, icon: TotalWorkflowIcon, className: 'review-completed-card' },
              { label: 'Draft Workflows', value: summaryResponse?.draft_workflows.total, icon: PendingActionIcon, className: 'pending-action-card' },
              { label: 'Returned for Changes', value: summaryResponse?.returned_for_changes_workflows.total, icon: ReturnChangesIcon, className: 'pending-workflow-card' },
            ];
            setSummaryData(tempData);
          } else {
            const tempData = [
              { label: 'Pending Action', value: 120, icon: PendingActionIcon, className: 'pending-action-card' },
              { label: 'Agency Submissions', value: 20, icon: AgencySubmissionIcon, className: 'agency-submissions-card' },
              { label: 'In Review', value: 18, icon: InReviewIcon, className: 'in-review-card' },
              { label: 'Overdue Workflows', value: 6, icon: OverdueWorkflowIcon, className: 'overdue-workflow-card' },
            ];
            setSummaryData(tempData);
          }
        }

        // getting verdict graph data
        if (summaryResponse.material_verdicts) {
          summaryResponse.material_verdicts = Object.fromEntries(
            Object.entries(summaryResponse.material_verdicts).map(([key, value]) => [
              key === 'in_discussion' ? 'needs_meeting' : key,
              value
            ])
          );
          setVerdictGraphData(summaryResponse.material_verdicts);
        }

        // getting pending workflow graph data
        if (summaryResponse.pending_workflows_per_reviewer) {
          const tempRevs = Object.keys(summaryResponse.pending_workflows_per_reviewer);
          if (tempRevs && tempRevs.length > 0) {
            getUserBatch(tempRevs)?.then((res: any) => {
              const updatedData: any = Object.entries(summaryResponse.pending_workflows_per_reviewer).map(([id, value]: any) => {
                const tempUser = res?.find((u: any) => u.id === id);
                const fullName = tempUser ? `${tempUser.first_name} ${tempUser.last_name}` : id;

                return {
                  id,
                  fullName,
                  total: value.total
                };
              }).sort((a: any, b: any) => b.total - a.total);

              setPendingWorkflowGraphData(updatedData);
            });
          } else {
            setPendingWorkflowGraphData([]);
          }
        }

        // Workflow Table Data for admin/marcom
        if (adminRole) {
          // Other Workflow
          if (summaryResponse.other_workflows) {
            setTotalRecords(summaryResponse.other_workflows.total_items);
            let tempUsers = summaryResponse.other_workflows.workflows.map((a: any) => a.created_by_user_id);
            if (tempUsers && tempUsers.length > 0) {
              getUserBatch(tempUsers)?.then((res: any) => {
                let tempWorkflows: any = [];
                summaryResponse.other_workflows.workflows.forEach((element: any, i: any) => {
                  tempWorkflows.push({
                    ...element,
                    fullName: `${res?.[i]?.first_name} ${res?.[i]?.last_name}`,
                    nameInitials: getInitials(`${res?.[i]?.first_name} ${res?.[i]?.last_name}`)
                  });
                });
                setAllWorkflows(tempWorkflows);
              });
            }
          }

          // assigned to me workflows
          if (summaryResponse.non_reviewer_workflows) {
            setAssignedWorkflowCount(summaryResponse.non_reviewer_workflows.total_items);
            let tempUsers = summaryResponse.non_reviewer_workflows.workflows.map((a: any) => a.created_by_user_id);
            if (tempUsers && tempUsers.length > 0) {
              getUserBatch(tempUsers)?.then((res: any) => {
                let tempWorkflows: any = [];
                summaryResponse.non_reviewer_workflows.workflows.forEach((element: any, i: any) => {
                  tempWorkflows.push({
                    ...element,
                    fullName: `${res[i].first_name} ${res[i].last_name}`,
                    nameInitials: getInitials(`${res[i].first_name} ${res[i].last_name}`)
                  });
                });
                setAllAssignedWorkflows(tempWorkflows);
              });
            }
          }
        }

        // Workflow Table Data for Brand Owner and Agency
        if (boAgencyRole) {
          // Active Workflow
          if (summaryResponse.active_workflows) {
            setActiveWorkflowsCount(summaryResponse.active_workflows.total_items);
            let tempUsers = summaryResponse.active_workflows.workflows.map((a: any) => a.created_by_user_id);
            if (tempUsers && tempUsers.length > 0) {
              getUserBatch(tempUsers)?.then((res: any) => {
                let tempWorkflows: any = [];
                summaryResponse.active_workflows.workflows.forEach((element: any, i: any) => {
                  tempWorkflows.push({
                    ...element,
                    fullName: `${res[i]?.first_name} ${res[i]?.last_name}`,
                    nameInitials: getInitials(`${res[i]?.first_name} ${res[i]?.last_name}`)
                  });
                });
                setAllActiveWorkflows(tempWorkflows);
              });
            }
          }

          // Completed Workflow
          if (summaryResponse.complete_workflows) {
            setCompleteWorkflowsCount(summaryResponse.complete_workflows.total_items);
            let tempUsers = summaryResponse.complete_workflows.workflows.map((a: any) => a.created_by_user_id);
            if (tempUsers && tempUsers.length > 0) {
              getUserBatch(tempUsers)?.then((res: any) => {
                let tempWorkflows: any = [];
                summaryResponse.complete_workflows.workflows.forEach((element: any, i: any) => {
                  tempWorkflows.push({
                    ...element,
                    fullName: `${res[i]?.first_name} ${res[i]?.last_name}`,
                    nameInitials: getInitials(`${res[i]?.first_name} ${res[i]?.last_name}`)
                  });
                });
                setAllCompleteWorkflows(tempWorkflows);
              });
            }
          }
        }

        // Workflow Table Data for Reviewer
        if (reviewerRole) {
          // All Workflow
          if (summaryResponse.all_workflows) {
            setTotalRecords(summaryResponse.all_workflows.total_items);
            let tempUsers = summaryResponse.all_workflows.workflows.map((a: any) => a.created_by_user_id);

            if (tempUsers && tempUsers.length > 0) {
              getUserBatch(tempUsers)?.then((res: any) => {
                let tempWorkflows: any = [];
                summaryResponse.all_workflows.workflows.forEach((element: any, i: any) => {
                  tempWorkflows.push({
                    ...element,
                    fullName: `${res[i]?.first_name} ${res[i]?.last_name}`,
                    nameInitials: getInitials(`${res[i]?.first_name} ${res[i]?.last_name}`)
                  });
                });
                setAllWorkflows(tempWorkflows);
              });
            }
          }

          // Pending Action Workflow
          if (summaryResponse.pending_action_workflows) {
            setPendingReview(summaryResponse.pending_action_workflows.total_items);
            let tempUsers = summaryResponse.pending_action_workflows.workflows.map((a: any) => a.created_by_user_id);

            if (tempUsers && tempUsers.length > 0) {
              getUserBatch(tempUsers)?.then((res: any) => {
                let tempWorkflows: any = [];
                summaryResponse.pending_action_workflows.workflows.forEach((element: any, i: any) => {
                  tempWorkflows.push({
                    ...element,
                    fullName: `${res[i]?.first_name} ${res[i]?.last_name}`,
                    nameInitials: getInitials(`${res[i]?.first_name} ${res[i]?.last_name}`)
                  });
                });
                setPendingWorkflowData(tempWorkflows);
              });
            }
          }
        }

        // Activity Feed
        if (response.data.messages[2] && response.data.messages[2].length > 0) {
          let tempActivities: any = [];
          response.data.messages[2].forEach((element: any, i: any) => {
            let tempFullName = extractFirstMentionedFullName(element?.audit_log_message, element.at_mentions);
            tempActivities.push({
              ...element,
              fullName: tempFullName,
              nameInitials: tempFullName ? getInitials(tempFullName) : '',
            });
          });

          setUserActivityData(tempActivities);
          setFilteredActivityData(tempActivities);
        }

        // Mention List Data
        if (!adminRole && response.data.messages[3] && response.data.messages[3].length > 0) {
          let tempMentions: any = [];
          response.data.messages[3].forEach((element: any, i: any) => {
            let tempFullName = extractFirstMentionedFullName(element?.audit_log_message, element.at_mentions);
            tempMentions.push({
              ...element,
              fullName: tempFullName,
              nameInitials: tempFullName ? getInitials(tempFullName) : '',
            });
          });
          setMentionData(tempMentions);
        }

        if (response.data.messages.total_items === 0) {
          setPendingReview(0);
          setAllWorkflows([]);
          setUserActivityData([]);
          setFilteredActivityData([]);
        }
      }
    }).catch((error: any) => {
      if ([401, 404, 500].includes(error?.request?.status)) {
        handleError(error, toastBottom);
      }

      setisLoading(false);
    });
  };

  const onPage = (event: any) => {
    setlazyState(event);
  };

  const timePeriodChangedRef = useRef(false);
  const prevPageRef = useRef(lazyState.page);

  useEffect(() => {
    timePeriodChangedRef.current = true;
    setlazyState((prev) => ({ ...prev, page: 0 }));
  }, [timePeriodValue]);

  const prevFilterRef = useRef(filterTable);

  useEffect(() => {
    const isFilterChanged = prevFilterRef.current !== filterTable;

    if (isFilterChanged) {
      getAllData();
    }

    prevFilterRef.current = filterTable;
  }, [filterTable, lazyState.page]);

  useEffect(() => {
    const isSortChanged = prevSortRef.current.sortField !== lazyState.sortField
      || prevSortRef.current.sortOrder !== lazyState.sortOrder;

    const isPageChanged = prevPageRef.current !== lazyState.page;

    const shouldFetch = timePeriodChangedRef.current || isSortChanged || isPageChanged;

    if (shouldFetch) {
      if (isSortChanged || isPageChanged) {
        getAllData(isSortChanged, true);
      } else {
        getAllData(isSortChanged);
      }
      timePeriodChangedRef.current = false;
      prevPageRef.current = lazyState.page;
    }

    prevSortRef.current = {
      sortField: lazyState.sortField,
      sortOrder: lazyState.sortOrder,
    };
  }, [lazyState, timePeriodValue]);

  useEffect(() => {
    prevSortRef.current = {
      sortField: lazyState.sortField,
      sortOrder: lazyState.sortOrder,
    };
  }, [lazyState]);

  const onBrandGuidelines = () => {
    window.open('https://identitynet.bayer.com/portal/', '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="home-container" role="home">
      <Toast ref={toast} />
      <Toast ref={toastBottom} position="bottom-right" />

      {/*
      {isLoading && (
        <div className="card">
          <SpinnerOverlay />
        </div>
      )}
      */}

      <div className="dashboard-header-section flex justify-content-between align-items-center">
        <div>
          <h2 className="homepage-title mb-0 mt-0">Dashboard</h2>
        </div>
        <div className="flex align-items-center gap-3">
          {['agency'].includes(user.role) && (
            <div className="brand-guidelines" onClick={() => onBrandGuidelines()}>
              Brand Guidelines
            </div>
          )}
          <Dropdown
            value={timePeriodValue}
            options={timePeriodOptions}
            onChange={(e) => {
              if (reviewerRole) {
                setToggleFilterTable('pending_review');
              } else if (boAgencyRole) {
                setToggleFilterTable('active_workflows');
              } else if (adminRole) {
                setToggleFilterTable('assigned_to_me');
              }
              setTimePeriodValue(e.value);
              setlazyState((prevState) => ({
                ...prevState,
                page: 0
              }));
            }}
            placeholder="Time period"
            optionLabel="name"
            appendTo="self"
          />
        </div>
      </div>

      {['admin', 'marcom', 'brand_owner'].includes(user.role) && (
        <div className="dashboard-top-section flex">
          {summaryData.map((stat: any, i: any) => (
            <div key={i} className={`flex-1 dashboard-top-box justify-content-between align-items-center ${stat.className}`}>
              <div style={{ flexBasis: '77%' }}>
                <div className="top-box-value">{stat.value}</div>
                <div className="top-box-status">{stat.label}</div>
              </div>
              <div className="top-box-icon-container flex justify-content-end" style={{ flexBasis: '20%' }}>
                <div className="top-box-icon">
                  <img src={stat.icon} alt="icon" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="dashboard-main-content">
        <div className="dashboard-main-left">
          {['reviewer', 'agency'].includes(user.role) && (
            <div className="dashboard-top-section flex mt-0" style={{ marginBottom: '20px' }}>
              {summaryData.map((stat: any, i: any) => (
                <div key={i} className={`flex-1 dashboard-top-box justify-content-between align-items-center ${stat.className}`}>
                  <div style={{ flexBasis: '77%' }}>
                    <div className="top-box-value">{stat.value}</div>
                    <div className="top-box-status">{stat.label}</div>
                  </div>
                  <div className="top-box-icon-container flex justify-content-end" style={{ flexBasis: '20%' }}>
                    <div className="top-box-icon">
                      <img src={stat.icon} alt="icon" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="dashboard-card">
            <Suspense fallback={<SpinnerOverlay />}>
              <WorkflowTable
                pendingReview={pendingReview}
                totalRecords={totalRecords}
                assignedWorkflowCount={assignedWorkflowCount}
                activeWorkflowsCount={activeWorkflowsCount}
                completeWorkflowsCount={completeWorkflowsCount}
                toggleFilterTable={toggleFilterTable}
                allAssignedWorkflows={allAssignedWorkflows}
                pendingWorkflowData={pendingWorkflowData}
                allActiveWorkflows={allActiveWorkflows}
                allCompleteWorkflows={allCompleteWorkflows}
                allWorkflows={allWorkflows}
                lazyState={lazyState}
                onTogglePageChange={onTogglePageChange}
                onPage={onPage}
                onSort={onSort}
                apiLoading={apiLoading}
              />
            </Suspense>
          </div>
          {!['admin', 'marcom', 'reviewer'].includes(user.role) && (
            <div className="dashboard-card">
              <Suspense fallback={<SpinnerOverlay />}>
                <MentionList mentionListData={mentionData} apiLoading={apiLoading} />
              </Suspense>
            </div>
          )}
        </div>
        <div className="dashboard-main-right">
          {['reviewer'].includes(user.role) && (
            <div className="dashboard-card">
              <Suspense fallback={<SpinnerOverlay />}>
                <MentionList mentionListData={mentionData} apiLoading={apiLoading} />
              </Suspense>
            </div>
          )}

          {['admin', 'marcom', 'brand_owner', 'agency'].includes(user.role) && (
            <div className="dashboard-card mb-15">
              <Suspense fallback={<SpinnerOverlay />}>
                <VerdictGraph graphData={verdictGraphData} apiLoading={apiLoading} />
              </Suspense>
            </div>
          )}
          {['admin', 'marcom'].includes(user.role) && (
            <div className="dashboard-card mb-15">
              <Suspense fallback={<SpinnerOverlay />}>
                <PendingWorkflowGraph graphData={pendingWorkflowGraphData} apiLoading={apiLoading} />
              </Suspense>
            </div>
          )}
          {!['admin', 'marcom'].includes(user.role) && (
            <div className="dashboard-card mb-15">
              <Suspense fallback={<SpinnerOverlay />}>
                <ActivityFeed
                  filteredActivityData={filteredActivityData}
                  activityFilterOptions={activityFilterOptions}
                  activityFilterCategory={activityFilterCategory}
                  onActivityFilterCategoryChange={onActivityFilterCategoryChange}
                  apiLoading={apiLoading}
                />
              </Suspense>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Home;
